import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Plus, Crown, User, Pencil, Loader2, Sparkles } from 'lucide-react';
import { SpecialistIcon } from './specialistIcons';
import MemberAvatar from './MemberAvatar';
import { useCatalogAvatars } from './useCatalogAvatars';
import GroupAvatar from './GroupAvatar';
import AgentDetailPanel from './AgentDetailPanel';
import SheetOrPage from './SheetOrPage';
import LogoCropper from './LogoCropper';
import ChatStorageSection from './ChatStorageSection';
import AutonomySettings from './AutonomySettings';
import ChatLanguageSetting from './ChatLanguageSetting';
import TopUpCard from '../wallet/TopUpCard';
import { generateConversationLogo, LOGO_STYLES } from '../../services/conversationService';
import { getAgentProfiles } from '../../services/agentService';
import { uploadToPath, resolveDownloadUrl } from '../../services/storageService';
import {
  addConversationMember, removeConversationMember, removeConversationParticipant,
  setConversationLogo, renameConversation,
  type ConversationSummary, type ConversationMember,
} from '../../services/conversationService';
import { GROUP_MAX, type ChatSession, type Specialist, type MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

interface Props {
  open: boolean;
  session: ChatSession | null;
  isOwner: boolean;
  onClose: () => void;
  onUpdated: (c: ConversationSummary) => void;
  /** Refresh the wallet balance after a spend. */
  onWalletChanged?: () => void;
  /** Mobile → render as a full-screen page instead of a modal. */
  asPage?: boolean;
}

const toMember = (s: Specialist): ConversationMember => ({
  id: s.id, name: s.name, persona: s.persona, avatar_icon: s.avatar_icon,
  profile_id: s.id, model_tier: s.model_tier, temperature: s.temperature,
});

const ManageMembersPanel: React.FC<Props> = ({ open, session, isOwner, onClose, onUpdated, onWalletChanged, asPage }) => {
  const catalogAvatars = useCatalogAvatars(); // admin default avatars, keyed by profile_id
  const [catalog, setCatalog] = useState<Specialist[]>([]);
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [editing, setEditing] = useState<MemberSpec | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoPrompt, setLogoPrompt] = useState('');
  const [logoStyle, setLogoStyle] = useState('minimal');
  const [logoNeedTopUp, setLogoNeedTopUp] = useState(false);
  const logoFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(session?.title || '');
    setAdding(false); setAddQuery(''); setError(''); setLogoUrl(session?.logoUrl || ''); setEditing(null); setLogoFile(null);
    setLogoPrompt(''); setLogoStyle('minimal'); setLogoNeedTopUp(false);
    getAgentProfiles().then((l) => setCatalog(l || [])).catch(() => setCatalog([]));
  }, [open, session?.logoUrl, session?.title]);

  if (!open || !session) return null;
  const ownerUid = session.ownerId || '';

  /** Crop confirmed → upload the square PNG to chat-logos, then persist the URL. */
  const saveCroppedLogo = async (blob: Blob) => {
    setLogoBusy(true); setError('');
    try {
      const path = `chat-logos/${ownerUid}/${session.id}/logo_${Date.now()}.png`;
      await uploadToPath(path, blob, 'image/png');
      const url = await resolveDownloadUrl(path);
      onUpdated(await setConversationLogo(session.id, url));
      setLogoUrl(url); setLogoFile(null);
    } catch (e: any) { setError(e?.message || 'Could not upload the logo'); } finally { setLogoBusy(false); }
  };

  /** Generate a logo from a prompt (charges credits), then open the cropper on the result. */
  const generateLogo = async () => {
    setLogoBusy(true); setError(''); setLogoNeedTopUp(false);
    try {
      const { imageBase64, mime } = await generateConversationLogo(session.id, logoPrompt.trim() || undefined, logoStyle);
      const bytes = Uint8Array.from(atob(imageBase64), (c) => c.charCodeAt(0));
      setLogoFile(new File([bytes], 'logo.png', { type: mime || 'image/png' })); // → LogoCropper
      onWalletChanged?.();
    } catch (e: any) {
      if (/not enough/i.test(e?.message || '')) setLogoNeedTopUp(true);
      else setError(e?.message || 'Logo generation failed');
    } finally { setLogoBusy(false); }
  };

  const run = async (fn: () => Promise<ConversationSummary>) => {
    setBusy(true); setError('');
    try { onUpdated(await fn()); } catch (e: any) { setError(e?.message || 'Something went wrong'); } finally { setBusy(false); }
  };

  const inChat = new Set(session.members.map((m) => m.id));
  const addable = catalog.filter((s) => !inChat.has(s.id));
  const atMax = session.members.length >= GROUP_MAX;
  // Search across name / persona / description / capabilities so the user can FIND the right
  // specialist (e.g. an agent referred them to "a video" or "a mathematician") instead of
  // scanning the whole pack.
  const q = addQuery.trim().toLowerCase();
  const addMatches = q
    ? addable.filter((s) => `${s.name} ${s.persona} ${(s as any).description || ''} ${((s as any).capabilities || []).join(' ')}`.toLowerCase().includes(q))
    : addable;

  return (
    <>
      <SheetOrPage open={open} asPage={asPage} title="Manage group" onClose={onClose} maxWidth="max-w-2xl">
        <div className="space-y-6">
          {error && <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

          {/* Group logo (above the name) — its initial-character icon until the owner makes a logo.
              Generate from a prompt (PRIMARY) or upload (secondary). Owner only. */}
          {isOwner && (
            <section>
              <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Group logo</p>
              <div className="flex items-start gap-4">
                {/* The fallback below is a single initial sized to its 64px circle — a glyph, not text, so it
                    sits outside the type scale by design (typeScale.ts states that exemption). Do not "fix". */}
                {logoUrl
                  ? <img src={logoUrl} alt="Group logo" className="w-16 h-16 shrink-0 rounded-full object-cover border border-secondary-200 dark:border-secondary-600" />
                  : <GroupAvatar name={title || session.title} className="w-16 h-16 text-xl" />}

                <div className="flex-1 min-w-0">
                  <label className={`${TYPE.control} text-secondary-500 dark:text-secondary-300`}>Describe your logo and let AI create it</label>
                  {/* Style picker — a logo can be a minimal mark, an emblem, a landscape, a 3D render, etc. */}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {LOGO_STYLES.map((s) => (
                      <button key={s.key} type="button" onClick={() => setLogoStyle(s.key)}
                        className={`rounded-full px-2.5 py-1 ${TYPE.control} border transition-colors ${logoStyle === s.key
                          ? 'border-secondary-900 dark:border-white bg-secondary-900 dark:bg-white text-white dark:text-secondary-900'
                          : 'border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-300 hover:border-secondary-400'}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-1.5 flex gap-2">
                    <input value={logoPrompt} onChange={(e) => setLogoPrompt(e.target.value)} placeholder="e.g. a serene mountain lake at dawn"
                      className={`flex-1 min-w-0 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 ${TYPE.body} text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500`} />
                    <button disabled={logoBusy} onClick={generateLogo}
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2 ${TYPE.body} font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40`}>
                      {logoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Generate
                    </button>
                  </div>
                  <div className={`mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 ${TYPE.control}`}>
                    <button disabled={busy} onClick={() => logoFileRef.current?.click()}
                      className="text-secondary-500 hover:text-secondary-900 dark:hover:text-white disabled:opacity-40">
                      {logoUrl ? 'Upload a different image' : 'Or upload an image instead'}
                    </button>
                    {logoUrl && (
                      <button disabled={busy} onClick={() => run(async () => { const c = await setConversationLogo(session.id, null); setLogoUrl(''); return c; })}
                        className="text-secondary-500 hover:text-secondary-500 disabled:opacity-40">Remove logo</button>
                    )}
                  </div>
                  <input ref={logoFileRef} type="file" accept="image/*" hidden
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) setLogoFile(f); e.currentTarget.value = ''; }} />
                </div>
              </div>
              {logoNeedTopUp && <div className="mt-3"><TopUpCard /></div>}
              <p className={`mt-2 ${TYPE.control} text-secondary-400`}>Until you make a logo, the group shows its initial. Generated logos open in the cropper.</p>
            </section>
          )}

          {/* Group name — rename for a memorable shareable URL (/group/{name}). Owner only. */}
          {isOwner && (
            <section>
              <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Group name</p>
              <div className="flex gap-2">
                <input
                  value={title}
                  maxLength={80}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Name this group"
                  className={`flex-1 min-w-0 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 ${TYPE.body} text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500`}
                />
                <button
                  disabled={busy || !title.trim() || title.trim() === session.title}
                  onClick={() => run(() => renameConversation(session.id, title.trim()))}
                  className={`shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2 ${TYPE.body} font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40`}>
                  Rename
                </button>
              </div>
              {session.slug && <p className={`mt-1.5 ${TYPE.meta} text-secondary-400 break-all`}>Link: {typeof window !== 'undefined' ? window.location.origin : 'https://wisetree.ai'}/group/{session.slug}</p>}
            </section>
          )}

          {/* People */}
          <section>
            <p className={`mb-2 ${TYPE.label} text-secondary-400`}>People</p>
            <div className="space-y-1.5">
              {(session.humanMembers || []).map((h) => (
                <div key={h.uid} className={`flex items-center gap-2.5 ${TYPE.body}`}>
                  {h.role === 'owner' ? <Crown className="h-4 w-4 text-secondary-600 dark:text-secondary-300" /> : <User className="h-4 w-4 text-secondary-400" />}
                  <span className="flex-1 text-secondary-800 dark:text-secondary-100 truncate">{h.name}</span>
                  {isOwner && h.role !== 'owner' && (
                    <button disabled={busy} onClick={() => run(() => removeConversationParticipant(session.id, h.uid))} className="text-secondary-400 hover:text-secondary-500" aria-label={`Remove ${h.name}`} title={`Remove ${h.name}`}><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Specialists */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <p className={`${TYPE.label} text-secondary-400`}>Specialists ({session.members.length}/{GROUP_MAX})</p>
              {isOwner && !atMax && (
                <button onClick={() => setAdding((v) => !v)} className={`inline-flex items-center gap-1 ${TYPE.control} text-secondary-500 hover:text-secondary-900 dark:hover:text-white`}><Plus className="h-3.5 w-3.5" /> Add</button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {session.members.map((m) => (
                <div key={m.id} data-testid={`agent-tile-${m.id}`}
                  className="group relative flex flex-col items-center gap-1.5 rounded-xl border border-secondary-200 dark:border-secondary-600 p-3 text-center">
                  <button
                    type="button"
                    onClick={() => setEditing(m)}
                    className="flex flex-col items-center gap-1.5 w-full"
                    title={isOwner ? 'Edit this agent' : `${m.alias || m.name} details`}
                    aria-label={`${m.alias || m.name} details`}
                  >
                    <MemberAvatar avatarIcon={m.avatar_icon} avatarPath={m.avatar_path} avatarDriveFileId={m.avatar_drive_file_id} avatarImage={m.profile_id ? catalogAvatars[m.profile_id] : null} conversationId={session.id} name={m.alias || m.name} className="w-12 h-12" />
                    <span className={`${TYPE.control} font-medium text-secondary-800 dark:text-secondary-100 truncate w-full`}>{m.alias || m.name}</span>
                    <span className={`${TYPE.micro} text-secondary-400 truncate w-full`}>{m.persona}</span>
                  </button>
                  {isOwner && (
                    <Pencil className="h-3 w-3 absolute top-2 left-2 text-secondary-300 group-hover:text-secondary-500" />
                  )}
                  {isOwner && session.members.length > 1 && (
                    <button disabled={busy} onClick={() => run(() => removeConversationMember(session.id, m.id))}
                      className="absolute top-2 right-2 text-secondary-300 hover:text-secondary-500" aria-label={`Remove ${m.alias || m.name}`} title={`Remove ${m.alias || m.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
            {adding && (
              <div className="mt-2 rounded-lg border border-secondary-200 dark:border-secondary-600">
                <input
                  autoFocus
                  value={addQuery}
                  onChange={(e) => setAddQuery(e.target.value)}
                  placeholder="Search specialists — e.g. video, music, math, dating…"
                  className={`w-full rounded-t-lg border-b border-secondary-200 dark:border-secondary-600 bg-transparent px-3 py-2 ${TYPE.body} text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:outline-none`}
                />
                <div className="max-h-64 overflow-y-auto divide-y divide-secondary-100 dark:divide-secondary-800">
                  {addable.length === 0 && <p className={`px-3 py-2 ${TYPE.control} text-secondary-400`}>No more specialists to add.</p>}
                  {addable.length > 0 && addMatches.length === 0 && <p className={`px-3 py-2 ${TYPE.control} text-secondary-400`}>No specialists match “{addQuery}”.</p>}
                  {addMatches.map((s) => (
                    <button key={s.id} disabled={busy || atMax} onClick={() => run(() => addConversationMember(session.id, toMember(s)))}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.body} hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-40`}>
                      <SpecialistIcon name={s.avatar_icon} className="h-4 w-4 shrink-0 text-secondary-500" />
                      <span className="min-w-0 text-secondary-800 dark:text-secondary-100">
                        {s.name} <span className="text-secondary-400">· {s.persona}</span>
                        {(s as any).description && <span className={`block truncate ${TYPE.meta} text-secondary-400`}>{(s as any).description}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {isOwner && (
            <>
              {/* Storage — Wisetree Cloud ⇄ the owner's Google Drive */}
              <ChatStorageSection session={session} />

              {/* Group main language — agents reply in it by default */}
              <ChatLanguageSetting session={session} />

              {/* Proactive autonomy — free ambient news + charged watch-mode discussion */}
              <AutonomySettings session={session} />
            </>
          )}
        </div>
      </SheetOrPage>

      <AgentDetailPanel
        open={!!editing}
        session={session}
        member={editing}
        isOwner={isOwner}
        onWalletChanged={onWalletChanged}
        onClose={() => setEditing(null)}
        onUpdated={(c) => { onUpdated(c); setEditing(null); }}
      />

      {logoFile && (
        <LogoCropper file={logoFile} busy={logoBusy} onCancel={() => setLogoFile(null)} onCrop={saveCroppedLogo} />
      )}
    </>
  );
};

export default ManageMembersPanel;
